import { globalEnv } from "~/global-env";
import {
  ROLES_WITH_POSITIONS_MANAGE_PERMISSION,
  WorkspaceRoleGroupNames,
} from "~/pages/UserAndRoles/WorkspaceRolesPage/WorkspaceRoleDetails/components/PermissionsTab/consts";
import { User, UserRoles } from "~/types/user";

const FUND_ADMIN_ROLES = [
  "JSQ Fund Accountant",
  "JSQ Fund Accountant Manager",
  "JSQ Fund Accounting Onboarding",
  "JSQ Billing Manager",
];

const HAS_ACCESS_TO_SYNC_TRANSFERS = [
  "JSQ Fund Accountant",
  "JSQ Fund Accountant Manager",
  "JSQ Fund Accounting Onboarding",
];

const ADMIN_ROLE = "Admin";

const SERVICE_REQUEST_ROLE = "Service Request User";
const COMPLIANCE_ROLES = ["Compliance Analyst", "Compliance Manager"];
const COMPLIANCE_MANAGER_ROLE = "Compliance Manager";
const TREASURY_ROLE = "Treasury Payments Admin";
const TREASURY_ANALYST_ROLE = "Treasury Analyst";
const ENABLEMENT_ROLE = "Announcement Enabler";
const FUND_ACCOUNTING_CONTROLLER_ROLE = "JSQ Fund Accounting Controller";
const BILLING_ROLES = [
  "JSQ Billing Manager",
  "JSQ Fund Accountant",
  "JSQ Billing FA Maker",
  "JSQ Billing FA Checker",
  "JSQ Billing FA Admin",
  "Billing CSM Manager",
  "Billing CSM IC",
  "GP Billing Admin",
];
const BILLING_MANAGER_ROLE = "JSQ Billing Manager";
const BILLING_ADMIN_ROLE = "JSQ Billing FA Admin";
const BILLING_MAKER_ROLE = "JSQ Billing FA Maker";
const BILLING_CHECKER_ROLES = ["JSQ Billing FA Checker", "JSQ Billing FA Admin"];
const BILLING_FA_ROLES = [
  // JSQ Fund Accountant is omitted on purpose because it was used on phase 1
  "JSQ Billing FA Maker",
  "JSQ Billing FA Checker",
  "JSQ Billing FA Admin",
];

// GP Billing Center personas — CSM IC/Manager are provisioned as new Canopy roles.
// "GP Billing Admin" is the display name for jsq_billing_gp_admin (seeded by migration #42019).
export const GP_BILLING_ADMIN_ROLE = "GP Billing Admin";
export const BILLING_CSM_MANAGER_ROLE = "Billing CSM Manager";
export const BILLING_CSM_IC_ROLE = "Billing CSM IC";
const GP_BILLING_ROLES = [BILLING_CSM_MANAGER_ROLE, BILLING_CSM_IC_ROLE, GP_BILLING_ADMIN_ROLE];
// GP checker legs: CSM Manager (secondary) + GP Billing Admin (primary); CSM IC is maker-only.
const GP_BILLING_CHECKER_ROLES = [BILLING_CSM_MANAGER_ROLE, GP_BILLING_ADMIN_ROLE];
const DATA_VAULT_ROLES = ["Data Vault Admin", "Data Vault User"];
const DATA_VAULT_ADMIN_ROLES = ["Data Vault Admin"];
const DATA_VAULT_USER_ROLES = ["Data Vault User"];
// Data Studio role constants - exported for use across the application
export const DATA_STUDIO_FUND_ADMIN_MANAGER_ROLE = "JSQ Data Onboarding Fund Admin Manager";
export const DATA_STUDIO_USER_ROLE = "JSQ Data Onboarding User";
export const DATA_STUDIO_MANAGER_ROLE = "JSQ Data Onboarding Manager";

const DATA_STUDIO_ROLES = [
  DATA_STUDIO_FUND_ADMIN_MANAGER_ROLE,
  DATA_STUDIO_USER_ROLE,
  DATA_STUDIO_MANAGER_ROLE,
];

// OpCo Import permission - matches JsqAclPermission.access_opco_import
export const ACCESS_OPCO_IMPORT_PERMISSION = "access_opco_import";

// Only Service Request User role is mapped to access_opco_import in the backend
export const SERVICE_REQUEST_USER_ROLE = "Service Request User";

export const OPCO_IMPORT_ROLES = [SERVICE_REQUEST_USER_ROLE];

const CONTROL_TOWER_ROLES = [
  "JSQ Fund Accountant Manager",
  "Service Request User",
  "JSQ Fund Accountant",
];

const REPORTING_GLOBAL_SETTINGS_ROLE = "JSQ Global Reporting Settings";

const CONTROL_TOWER_VIEW_ALL_PERMISSION = "Control Tower View All";
const CONTROL_TOWER_MANAGER_VIEW_PERMISSION = "Control Tower Manager View";
const TREASURY_PLATFORM_MANAGEMENT_ROLE = "CAN_MANAGE_TREASURY_PLATFORM";
const TREASURY_ONBOARDING_ROLE = "JSQ Treasury Onboarding User";
const TREASURY_TROUBLESHOOTING_ROLE = "JSQ Treasury Troubleshooting User";
const CANOPY_ANALYST_ROLE = "Canopy Analyst";

export const VIEW_AND_EDIT_POSITIONS_IN_JSQ_ADMINISTERED_ENTITIES_PERMISSION =
  "View and edit positions in JSQ administered entities";

// Workspace access roles
export const GP_WORKSPACE_ACCESS_PROVISION_ROLE = "GP Workspaces Access - Provision";

export const JUNIPER_SQUARE_OPERATIONS_MANAGER_ROLE = "Juniper Square Operations Manager";

type RouteOptions = { featureFlags?: { [k: string]: boolean } };

export const isAdminUser = (userRoles: UserRoles) => {
  return userRoles?.includes(ADMIN_ROLE) || false;
};

// No ProdOps/internal-operator claim from Main yet, so this gates on Admin.
export const isDdqProdOpsUser = (userRoles: UserRoles): boolean => {
  return isAdminUser(userRoles);
};

export const isServiceRequestAdmin = (user: User) => {
  return user.roles?.includes(SERVICE_REQUEST_ROLE);
};

export const isFundAdmin = (user: User) => {
  return user.roles?.some((role) => role && FUND_ADMIN_ROLES.includes(role));
};

export const isFundAccountingController = (userRoles: UserRoles) => {
  return userRoles?.includes(FUND_ACCOUNTING_CONTROLLER_ROLE);
};

export const isComplianceUser = (userRoles: UserRoles) => {
  return userRoles?.some((role) => role && COMPLIANCE_ROLES.includes(role));
};

export const isComplianceManager = (userRoles: UserRoles) => {
  return userRoles?.includes(COMPLIANCE_MANAGER_ROLE);
};

export const isEnablementUser = (userRoles: UserRoles) => {
  return userRoles?.includes(ENABLEMENT_ROLE);
};

export const isTreasuryUser = (userRoles?: UserRoles) => {
  return userRoles?.includes(TREASURY_ROLE) || false;
};

export const isTransferSyncUser = (userRoles: UserRoles) => {
  return userRoles?.some((role) => role && HAS_ACCESS_TO_SYNC_TRANSFERS.includes(role));
};

export const getRedirectPath = (user: User, options?: RouteOptions) => {
  const workspaceFF = options?.featureFlags?.canopyWorkspaceAccessFF;
  if (workspaceFF) {
    return globalEnv.yourWorkspacesRoute;
  }
  // TODO: Add other roles here, for now fallback to fund accounting route
  return globalEnv.fundAccountingRoute;
};

export const isBillingUser = (userRoles: UserRoles) => {
  return userRoles?.some((role) => role && BILLING_ROLES.includes(role));
};

export const isBillingManager = (userRoles: UserRoles) =>
  userRoles?.includes(BILLING_MANAGER_ROLE);

export const isGpBillingAdmin = (userRoles: UserRoles): boolean =>
  userRoles?.includes(GP_BILLING_ADMIN_ROLE) || false;

// Mirrors the backend canopy export trigger gate (HasAnyRole(BILLING_ADMIN,
// GP_BILLING_MANAGER)); keep the two in sync.
export const canTriggerBillingExports = (userRoles: UserRoles): boolean =>
  isBillingManager(userRoles) || isGpBillingAdmin(userRoles);

export const isBillingChecker = (userRoles: UserRoles): boolean =>
  userRoles?.some((role) => role && BILLING_CHECKER_ROLES.includes(role)) || false;

export const isBillingAdmin = (userRoles: UserRoles): boolean =>
  userRoles?.includes(BILLING_ADMIN_ROLE) || false;

export const isBillingMaker = (userRoles: UserRoles): boolean =>
  userRoles?.includes(BILLING_MAKER_ROLE) || false;
export const hasBillingFARole = (userRoles: UserRoles): boolean =>
  userRoles?.some((role) => role && BILLING_FA_ROLES.includes(role)) || false;

export const hasBillingGPRole = (userRoles: UserRoles): boolean =>
  userRoles?.some((role) => role && GP_BILLING_ROLES.includes(role)) || false;

export const hasBillingGPCheckerRole = (userRoles: UserRoles): boolean =>
  userRoles?.some((role) => role && GP_BILLING_CHECKER_ROLES.includes(role)) || false;

export const isDataVaultUser = (userRoles: UserRoles) => {
  return userRoles?.some((role) => role && DATA_VAULT_ROLES.includes(role));
};

export const isDataVaultAdmin = (userRoles: UserRoles) => {
  return userRoles?.some((role) => role && DATA_VAULT_ADMIN_ROLES.includes(role));
};

export const isDataVaultUserOnly = (userRoles: UserRoles) => {
  return userRoles?.some((role) => role && DATA_VAULT_USER_ROLES.includes(role));
};

export const isTreasuryPlatformManager = (userRoles: UserRoles) => {
  return userRoles?.includes(TREASURY_PLATFORM_MANAGEMENT_ROLE) || false;
};

export const hasTreasuryTroubleshootingAccess = (userRoles: UserRoles): boolean => {
  return (
    isTreasuryPlatformManager(userRoles) ||
    userRoles?.includes(TREASURY_TROUBLESHOOTING_ROLE) ||
    false
  );
};

export const hasTreasuryOnboardingAccess = (userRoles: UserRoles): boolean => {
  return (
    isTreasuryPlatformManager(userRoles) ||
    hasTreasuryTroubleshootingAccess(userRoles) ||
    userRoles?.includes(TREASURY_ONBOARDING_ROLE) ||
    false
  );
};

export const hasTreasuryConfigAccess = (userRoles: UserRoles): boolean => {
  return (
    isTreasuryPlatformManager(userRoles) ||
    hasTreasuryOnboardingAccess(userRoles) ||
    hasTreasuryTroubleshootingAccess(userRoles)
  );
};

export const hasAnyTreasuryAccess = (userRoles: UserRoles) => {
  return isTreasuryUser(userRoles) || hasTreasuryConfigAccess(userRoles);
};

export const isTreasuryAdmin = (userRoles: UserRoles): boolean => {
  return userRoles?.includes(TREASURY_ROLE) || false;
};

// "Treasury Analyst" is granted on Arena 0 through the IS/ISM Canopy role groups,
// so its presence here only signals Treasury section visibility — per-workspace
// data scope is enforced server-side and never derived from this list.
export const hasTreasuryAccessOnAnyArena = (userRoles: UserRoles): boolean => {
  return isTreasuryAdmin(userRoles) || userRoles?.includes(TREASURY_ANALYST_ROLE) || false;
};

// Check if user has OpCo Import permission (via Service Request User role)
export const hasOpcoImportPermission = (userRoles: UserRoles): boolean => {
  return userRoles?.some((role) => role && OPCO_IMPORT_ROLES.includes(role)) || false;
};

export const isDataStudioUser = (userRoles: UserRoles) => {
  return (
    userRoles?.some((role) => role && DATA_STUDIO_ROLES.includes(role)) ||
    hasOpcoImportPermission(userRoles)
  );
};

export const isDataOnboardingFundAdminManager = (userRoles: UserRoles) => {
  return userRoles?.includes(DATA_STUDIO_FUND_ADMIN_MANAGER_ROLE);
};

export const isDataOnboardingUserOrManager = (userRoles: UserRoles) => {
  return (
    userRoles?.includes(DATA_STUDIO_USER_ROLE) || userRoles?.includes(DATA_STUDIO_MANAGER_ROLE)
  );
};

export const canSeeMyReviewsTab = (userRoles: UserRoles): boolean => {
  return userRoles?.includes(DATA_STUDIO_MANAGER_ROLE) || false;
};

export const isCanopyAnalyst = (userRoles: UserRoles): boolean => {
  return userRoles?.includes(CANOPY_ANALYST_ROLE) || false;
};

export const isGpWorkspaceAccessProvision = (userRoles: UserRoles): boolean => {
  return userRoles?.includes(GP_WORKSPACE_ACCESS_PROVISION_ROLE) || false;
};

export const isControlTowerUser = (userRoles: UserRoles): boolean => {
  return userRoles?.some((role) => role && CONTROL_TOWER_ROLES.includes(role)) || false;
};

export const canAccessManagerView = (userRoles: UserRoles): boolean => {
  return (
    userRoles?.includes(CONTROL_TOWER_VIEW_ALL_PERMISSION) ||
    userRoles?.includes(CONTROL_TOWER_MANAGER_VIEW_PERMISSION) ||
    false
  );
};

export const isDocumentExtractionUser = (userRoles: UserRoles): boolean => {
  const hasFundAdminRole = userRoles?.some((role) => role && FUND_ADMIN_ROLES.includes(role));
  const hasControllerRole = userRoles?.includes(FUND_ACCOUNTING_CONTROLLER_ROLE);
  return hasFundAdminRole || hasControllerRole || false;
};

// Fay borrows the document-extraction role check: Canopy has no Fay-specific role, and FA access
// here is not conditional on an arena's Fay enablement.
export const canAccessFayAI = (
  userRoles: UserRoles,
  isFayAIInCanopyEnabled: boolean,
): boolean => isFayAIInCanopyEnabled && isDocumentExtractionUser(userRoles);

export const isDexUser = (userRoles: UserRoles): boolean =>
  userRoles?.some((role) => role && FUND_ADMIN_ROLES.includes(role)) || false;

export const isJuniperSquareOperationsManagerRole = (userRole: string): boolean =>
  userRole === JUNIPER_SQUARE_OPERATIONS_MANAGER_ROLE;

export const hasManagePositionsPermission = (userRole: WorkspaceRoleGroupNames): boolean =>
  ROLES_WITH_POSITIONS_MANAGE_PERMISSION.includes(userRole);

export const hasGlobalReportingAccess = (userRoles: UserRoles): boolean =>
  userRoles?.includes(REPORTING_GLOBAL_SETTINGS_ROLE) || false;
